import { useStores } from '@littlebird/sdk';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { type MentionItem } from '@/features/mention';
import { logError } from '@/lib/errorLogging';

import {
  registerTeamChatMembers,
  type TeamChatMember,
} from './team-chat-members.store';
import { mentionedMemberIds } from './team-chat-mention-source';
import { firstNameOf } from './team-chat-messages';
import {
  invalidateTeamChat,
  useDiscardTeamChatDraftMutation,
  usePostToTeamChatMutation,
  useRequestTeamChatDraftMutation,
  useTeamChatDraftQuery,
} from './team-chats.query';

export type TeamChatComposerConfig = {
  readonly projectId: string;
  readonly chatId: string;
  readonly projectName: string;
  /** Everyone the viewer can mention, the viewer left out */
  readonly members: readonly TeamChatMember[];
  readonly viewerIsOwner: boolean;
};

export type TeamChatSend = 'post' | 'ask';

export type TeamChatHint = {
  readonly text: string;
  readonly action: {
    readonly label: string;
    readonly onClick: () => void;
  } | null;
};

const namesOf = (members: readonly TeamChatMember[], ids: number[]) =>
  members
    .filter((member) => ids.includes(member.userId))
    .map((member) => firstNameOf(member.name));

const joinNames = (names: string[]) =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;

const NO_MEMBERS: readonly TeamChatMember[] = [];

/** A team chat composer's state; null in any other composer */
export function useTeamChatComposer(
  config: TeamChatComposerConfig | undefined,
  draftId: string,
  mentions: readonly MentionItem[]
) {
  const { drafts: draftsStore } = useStores();
  const projectId = config?.projectId ?? '';
  const chatId = config?.chatId ?? '';
  const members = config?.members ?? NO_MEMBERS;
  const viewerIsOwner = config?.viewerIsOwner ?? false;
  const draftQuery = useTeamChatDraftQuery(config ? projectId : null, chatId);
  const requestDraft = useRequestTeamChatDraftMutation(projectId, chatId);
  const discardDraft = useDiscardTeamChatDraftMutation(projectId, chatId);
  const post = usePostToTeamChatMutation(projectId, chatId);
  // The text the viewer took from their Littlebird's draft: sending it posts it, with no turn
  const [acceptedText, setAcceptedText] = useState<string | null>(null);
  const [override, setOverride] = useState<TeamChatSend | null>(null);

  // The mention picker reads the members from outside React
  useEffect(
    () =>
      config === undefined
        ? undefined
        : registerTeamChatMembers(draftId, members),
    [config, draftId, members]
  );

  const draft = draftQuery.data ?? null;
  const mentionIds = mentionedMemberIds(mentions);
  const mentioned = joinNames(namesOf(members, mentionIds));
  const sendOf = (ids: number[]): TeamChatSend => {
    if (!viewerIsOwner || acceptedText !== null) return 'post';
    if (override !== null) return override;
    return ids.length > 0 ? 'post' : 'ask';
  };
  const send = sendOf(mentionIds);

  const hint = ((): TeamChatHint => {
    const everyone = `Everyone in ${config?.projectName ?? 'the project'} can see it.`;
    if (!viewerIsOwner)
      return {
        text:
          mentioned === ''
            ? `Posts as you. ${everyone}`
            : `Posts as you. ${mentioned} gets a notification and a draft reply.`,
        action: null,
      };
    if (send === 'ask')
      return {
        text: `Your Littlebird replies. ${everyone}`,
        action: {
          label: 'Post without a reply',
          onClick: () => setOverride('post'),
        },
      };
    return {
      text:
        mentioned === ''
          ? 'Posts as you. Your Littlebird stays quiet.'
          : `Goes to ${mentioned}. Your Littlebird stays quiet.`,
      action:
        acceptedText !== null
          ? null
          : {
              label: 'Ask Littlebird too',
              onClick: () => setOverride('ask'),
            },
    };
  })();

  const acceptDraft = (): boolean => {
    if (draft?.text === null || draft?.text === undefined) return false;
    void draftsStore.updateDraft({ draftId, content: draft.text });
    setAcceptedText(draft.text);
    return true;
  };

  const sendDraft = async (): Promise<boolean> => {
    if (draft?.text === null || draft?.text === undefined) return false;
    try {
      await post.mutateAsync({ text: draft.text, mentionIds: [] });
      invalidateTeamChat(projectId, chatId);
    } catch (error) {
      logError(error, 'Failed to send team chat draft');
      toast.error("Couldn't post the draft. Try again.");
    }
    return true;
  };

  const discard = () => {
    setAcceptedText(null);
    discardDraft.mutate(undefined, {
      onError: (error) => logError(error, 'Failed to discard team chat draft'),
    });
  };

  const onComposerText = (text: string) => {
    if (acceptedText !== null && text.trim() === '') discard();
  };

  const askForDraft = (instruction: string) => {
    requestDraft.mutate(instruction.trim() === '' ? null : instruction, {
      onError: (error) => {
        logError(error, 'Failed to request team chat draft');
        toast.error("Couldn't start a draft. Try again.");
      },
    });
  };

  // Posts the message when Send posts; 'ask' leaves it to the Littlebird send
  const submit = async (
    text: string,
    sentMentions: readonly MentionItem[],
    hasFiles: boolean
  ): Promise<'posted' | 'failed' | 'ask'> => {
    const ids = mentionedMemberIds(sentMentions);
    if (sendOf(ids) === 'ask') {
      setOverride(null);
      return 'ask';
    }
    // A post carries text only, so the files would be lost
    if (hasFiles) {
      toast.error(
        "Files can't be posted in a team chat yet. Remove them to post."
      );
      return 'failed';
    }
    try {
      await post.mutateAsync({ text, mentionIds: ids });
      setAcceptedText(null);
      setOverride(null);
      invalidateTeamChat(projectId, chatId);
      return 'posted';
    } catch (error) {
      logError(error, 'Failed to post in team chat');
      toast.error("Couldn't post. Your message is back in the box.", {
        action: {
          label: 'Try again',
          onClick: () =>
            void submit(text, sentMentions, false).then((outcome) => {
              const kept = draftsStore.getDraft(draftId);
              if (outcome === 'posted' && kept) draftsStore.removeDraft(kept);
            }),
        },
      });
      return 'failed';
    }
  };

  if (config === undefined) return null;
  return {
    draft,
    hint,
    acceptDraft,
    isEditingDraft: acceptedText !== null,
    sendDraft,
    discard,
    onComposerText,
    askForDraft,
    isPosting: post.isPending,
    submit,
  };
}

export type TeamChatComposerState = NonNullable<
  ReturnType<typeof useTeamChatComposer>
>;
